import React, {memo, useCallback} from "react";
import {Video} from "../../../../common/video/models/Video";
import styles from "./VideoCard.module.css";
import {Link, useNavigate} from "react-router-dom";
import HorizontalLine from "../../../../common/components/horizontalLine/HorizontalLine";
import VideoMetadataLine from "../../../../common/components/videoMetadataLine/VideoMetadataLine";
import {VideoTag} from "../../../../common/video/models/VideoTag";
import Tag from "../../../../common/components/tag/Tag";
import {
    SpacedList,
    SpacedListItem
} from "../../../../common/components/spacedList/SpacedList";
import VideoPresenter from "../../../../common/components/video/presenter/VideoPresenter";
import VideoMenu from "../../../../common/components/videoMenu/VideoMenu";
import EditVideoTab from "src/components/EditVideo";
import LinearProgress from "@material-ui/core/LinearProgress";
import PuiTooltip from "../../../../components/elements/PuiTooltip ";
import PuiTypography from "../../../../components/elements/PuiTypography";
import {createStyles, makeStyles} from "@material-ui/core";
import SlideIcon from "../../../../pages/vod/Common/SlideIcon";
import NewIcon from "../../../../common/components/images/NewIcon";
import publicCaption from "../../../../assets/image/publicCaption.svg";
import useResponsive from "../../../../common/useResponsive";

interface VideoCardProps {
  video: Video;
  setMsg: (msg: string) => void;
}

const useStyles = makeStyles(() =>
    createStyles({
        barColorPrimary: {
            backgroundColor: "var(--red)"
        }
    })
);

function VideoCard({video, setMsg}: VideoCardProps) {
    const classes = useStyles();
    const history = useNavigate();
    const goToVideo = useCallback(
        () => history(`/video/${video.hash}`),
        [video, history]
    );
    const {isDesktop, isMobile} = useResponsive();

    // 視聴履歴の進捗インジケータを計算
    const normalise = (video: Video) => {
    // console.log(video)
        if (video.viewingActivities.length === 0) {
            return 0;
        } else {
            const progressMillis = video.viewingActivities[0].progressMillis;
            const isFinished = video.viewingActivities[0].isFinished;
            if (progressMillis !== null) {
                const rate = Math.floor(
                    (Number(progressMillis) / Number(video.durationMillis)) * 100
                );
                // console.log(rate)
                return rate;
            } else if (progressMillis === null && isFinished) {
                return 100;
            }
        }
    };

    return (
        <div className={styles.card}>
            <div className={styles.thumbnaildiv}>
                <img
                    className={styles.thumbnail}
                    src={video.imageUrl}
                    alt=""
                    onClick={goToVideo}
                />

                {/* ビデオ編集ボタン */}
                <div className={styles.editVideoIcon}>
                    <EditVideoTab video={video}/>
                </div>

                {video.slideFlg && <SlideIcon videoId={String(video.id)}/>}

                {/* 新規ビデオアイコン */}
                {video.visibleNewIcon && (
                    <div className={styles.newIcon}>
                        <NewIcon/>
                    </div>
                )}
            </div>
            <LinearProgress
                className={styles.progressBar}
                classes={{barColorPrimary: classes.barColorPrimary}}
                variant="determinate"
                value={normalise(video)}
            />

            <div className={styles.text}>
                <div onClick={goToVideo}>
                    <div className={styles.title}>
                        {video.publicCaption && (
                            <img alt="publicCaption" src={publicCaption} width="50px"/>
                        )}
                        <div
                            style={{
                                width: video.publicCaption ? "calc(100% - 55px)" : "100%"
                            }}
                        >
                            <Link to={`/video/${video.hash}`}>
                                <div
                                    className={styles.cardTitle}
                                    data-for="listtitle"
                                    data-tip={video.title}
                                >
                                    <PuiTypography size={isMobile ? 14 : 20} rows={2} bold={true}>
                                        {video.title}
                                    </PuiTypography>
                                </div>
                                <PuiTooltip id="listtitle"/>
                            </Link>
                        </div>
                    </div>
                    <VideoPresenter presenter={video.presenter}/>
                    <div className={styles.cardTags}>
                        {video.tags.length > 0 && (
                            <SpacedList>
                                {video.tags.map((tag: VideoTag) => (
                                    <SpacedListItem key={tag}>
                                        <Tag tag={tag}/>
                                    </SpacedListItem>
                                ))}
                            </SpacedList>
                        )}
                    </div>
                </div>

                <HorizontalLine/>

                <div className={styles.meta} onClick={goToVideo}>
                    <VideoMetadataLine 
                        variant={isDesktop ? "normal" : "compact"} 
                        video={video} 
                        fontSize={isMobile ? 12 : 14}
                    />
                </div>
            </div>

            <VideoMenu video={video} setMsg={setMsg}/>
        </div>
    );
}

export default memo(VideoCard);
